import { useEffect, useRef, useState } from "react";
import { Check, Loader2, Mic, Play, Square, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { useOrg } from "../org";

const MAX = 120;
const TYPES = ["audio/mp4", "audio/webm;codecs=opus", "audio/webm"];
const clock = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
const KIND = { follow_up: "Follow-up", care_need: "Care need", assignment: "Care assignment" };

const useRecorder = () => {
  const [state, setState] = useState("idle");
  const [secs, setSecs] = useState(0);
  const [clip, setClip] = useState(null);
  const rec = useRef(null);
  const timer = useRef(null);
  const stop = () => { if (rec.current?.state === "recording") rec.current.stop(); };
  useEffect(() => {
    const hide = () => { if (document.hidden) stop(); };
    document.addEventListener("visibilitychange", hide);
    window.addEventListener("pagehide", stop);
    return () => { clearInterval(timer.current); stop(); document.removeEventListener("visibilitychange", hide); window.removeEventListener("pagehide", stop); };
  }, []);
  const start = async () => {
    if (!window.MediaRecorder || !navigator.mediaDevices?.getUserMedia) { toast.error(window.isSecureContext ? "This browser can't record audio. Update iOS or your browser, or type a note." : "Recording needs a secure (https) page."); return; }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    } catch (e) {
      toast.error(e?.name === "NotAllowedError" ? "Microphone access is off. On iPhone: Settings → Safari → Microphone → Allow, then try again. You can also type a note."
        : e?.name === "NotFoundError" ? "No microphone was found on this device." : "The microphone is busy, perhaps with a call or another app. Try again in a moment.");
      return;
    }
    try {
      const type = TYPES.find((t) => MediaRecorder.isTypeSupported?.(t));
      const r = new MediaRecorder(stream, type ? { mimeType: type } : undefined);
      const chunks = [];
      const began = Date.now();
      r.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      r.onerror = () => { toast.error("Recording stopped unexpectedly. Whatever was captured is kept below."); };
      stream.getAudioTracks().forEach((tr) => { tr.onended = stop; });
      r.onstop = () => {
        clearInterval(timer.current);
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunks, { type: (r.mimeType || type || "audio/mp4").split(";")[0] });
        if (!blob.size) { toast.error("Nothing was recorded. Please try again."); setState("idle"); return; }
        setClip({ blob, url: URL.createObjectURL(blob), duration: Math.min((Date.now() - began) / 1000, MAX) });
        setState("done");
      };
      rec.current = r;
      r.start(1000);
      setSecs(0); setState("recording");
      timer.current = setInterval(() => { const s = (Date.now() - began) / 1000; setSecs(s); if (s >= MAX) r.stop(); }, 250);
    } catch { stream.getTracks().forEach((t) => t.stop()); toast.error("Recording couldn't start on this browser. You can still type a note."); }
  };
  const reset = () => { if (clip) URL.revokeObjectURL(clip.url); setClip(null); setState("idle"); setSecs(0); };
  return { state, secs, clip, start, stop, reset };
};

export const VoiceRecorder = ({ person, confidential = false, onResult }) => {
  const { api } = useOrg();
  const r = useRecorder();
  const [busy, setBusy] = useState(false);
  const [ai, setAi] = useState(null);
  const [drafts, setDrafts] = useState([]);
  useEffect(() => { api("/voice-settings").then((s) => setAi(s.ai_enabled && (!confidential || s.ai_confidential))).catch(() => setAi(false)); }, [api, confidential]);
  useEffect(() => { api(`/people/${person.id}/voice-drafts?confidential=${confidential}`).then(setDrafts).catch(() => {}); }, [api, person.id, confidential]);
  const upload = async () => {
    setBusy(true);
    const form = new FormData();
    form.append("file", r.clip.blob, `memo.${r.clip.blob.type.includes("webm") ? "webm" : "m4a"}`);
    form.append("duration", String(Math.max(r.clip.duration, 0.5)));
    form.append("confidential", String(confidential));
    try {
      const v = await api(`/people/${person.id}/voice-notes`, { method: "POST", form });
      toast.success(v.ai_status === "processing" ? "Recording saved. Transcribing now; you can leave this screen." : "Recording saved");
      onResult(v); r.reset();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="surface p-4 mt-4" data-testid="voice-recorder">
      {r.state === "idle" && drafts[0] && (
        <button onClick={() => onResult(drafts[0])} className="btn btn-outline btn-sm mb-3 w-full" data-testid="voice-resume-draft-btn">Continue your unsaved voice note from {new Date(drafts[0].created_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</button>
      )}
      {r.state === "idle" && (
        <div className="flex items-center gap-3">
          <button onClick={r.start} className="h-12 w-12 rounded-full bg-brand text-paper inline-flex items-center justify-center shrink-0" aria-label="Record a voice note" data-testid="voice-record-btn"><Mic size={20} /></button>
          <div><p className="t-body font-medium">Record a voice note</p><p className="t-meta">Up to 2 minutes. {ai === null ? "" : ai ? "KNOWN will transcribe it and suggest a summary for you to review." : "Transcription is off for your church, so the recording is saved on its own."}</p></div>
        </div>
      )}
      {r.state === "recording" && (
        <div className="flex items-center gap-3" data-testid="voice-recording">
          <button onClick={r.stop} className="h-12 w-12 rounded-full bg-ink text-paper inline-flex items-center justify-center shrink-0" aria-label="Stop recording" data-testid="voice-stop-btn"><Square size={18} /></button>
          <span className="h-2.5 w-2.5 rounded-full bg-[var(--copper,#D7784D)] animate-pulse" />
          <p className="t-body tabular-nums" data-testid="voice-timer">{clock(r.secs)} / 2:00</p>
        </div>
      )}
      {r.state === "done" && r.clip && (
        <div data-testid="voice-preview">
          <audio src={r.clip.url} controls className="w-full" data-testid="voice-preview-audio" />
          <div className="flex flex-wrap gap-2 mt-3">
            <button disabled={busy} onClick={upload} className="btn btn-primary btn-sm" data-testid="voice-use-btn">{busy ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : "Use this recording"}</button>
            <button disabled={busy} onClick={r.reset} className="btn btn-quiet btn-sm" data-testid="voice-redo-btn">Record again</button>
          </div>
        </div>
      )}
    </div>
  );
};

const Suggestion = ({ s, i, onChange }) => (
  <div className={`py-3 ${s.decision === "dismissed" ? "opacity-50" : ""}`} data-testid={`voice-suggestion-${i}`}>
    <p className="t-meta">{KIND[s.type]}{s.unclear ? " · needs a check" : ""}</p>
    <input value={s.title} onChange={(e) => onChange({ ...s, title: e.target.value })} disabled={s.decision === "dismissed"} className="field h-11 px-3 mt-1" data-testid={`voice-suggestion-title-${i}`} />
    {s.evidence && <p className="t-meta mt-1 italic">"{s.evidence}"</p>}
    {s.type !== "care_need" && (
      <label className="flex items-center gap-2 mt-2 t-meta">{s.type === "assignment" ? "Due" : "When"}
        <input type="date" value={s.due_on || ""} onChange={(e) => onChange({ ...s, due_on: e.target.value || null })} className="field h-10 px-2 !w-auto" data-testid={`voice-suggestion-date-${i}`} />
      </label>
    )}
    <div className="flex gap-2 mt-2">
      <button onClick={() => onChange({ ...s, decision: s.decision === "approved" ? null : "approved" })} aria-pressed={s.decision === "approved"} className="choice h-10 px-3 inline-flex items-center gap-1" data-testid={`voice-suggestion-approve-${i}`}><Check size={15} /> Approve</button>
      <button onClick={() => onChange({ ...s, decision: s.decision === "dismissed" ? null : "dismissed" })} aria-pressed={s.decision === "dismissed"} className="choice h-10 px-3 inline-flex items-center gap-1" data-testid={`voice-suggestion-dismiss-${i}`}><X size={15} /> Dismiss</button>
    </div>
  </div>
);

export const VoiceReview = ({ v, setV, allowed = ["follow_up", "care_need", "assignment"] }) => {
  const [orig, setOrig] = useState(false);
  const shown = v.suggestions.map((s, i) => [s, i]).filter(([s]) => allowed.includes(s.type));
  const change = (i, s) => setV({ ...v, suggestions: v.suggestions.map((x, j) => (j === i ? s : x)) });
  return (
    <div className="mt-4" data-testid="voice-review">
      {!!v.transcript_original && (
        <>
          <div className="flex items-center justify-between"><p className="t-meta">Transcript, editable</p>
            <button onClick={() => setOrig(!orig)} className="text-sm text-brand underline underline-offset-4" data-testid="voice-original-toggle">{orig ? "Hide original" : "Show original"}</button></div>
          <textarea value={v.transcript} onChange={(e) => setV({ ...v, transcript: e.target.value })} rows={4} className="field p-3 mt-1 text-sm leading-relaxed" data-testid="voice-transcript" />
          {orig && <p className="t-meta mt-2 whitespace-pre-wrap" data-testid="voice-transcript-original">{v.transcript_original}</p>}
        </>
      )}
      {!!v.unclear.length && (
        <div className="mt-3 rounded-xl bg-attention-soft p-3" data-testid="voice-unclear">
          <p className="t-meta font-medium text-ink">Please check</p>
          <ul className="list-disc ml-5 t-meta">{v.unclear.map((u) => <li key={u}>{u}</li>)}</ul>
        </div>
      )}
      {!!shown.length && (
        <div className="mt-3" data-testid="voice-suggestions">
          <p className="t-meta">Possible next steps. Nothing is created unless you approve it.</p>
          <div className="rule-list">{shown.map(([s, i]) => <Suggestion key={i} s={s} i={i} onChange={(n) => change(i, n)} />)}</div>
        </div>
      )}
    </div>
  );
};

export const VoicePlayback = ({ voice }) => {
  const { api, membership } = useOrg();
  const [url, setUrl] = useState(null);
  const [open, setOpen] = useState(false);
  const [gone, setGone] = useState(!voice.has_audio);
  const [deleted, setDeleted] = useState(false);
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  if (deleted) return <p className="t-meta mt-2" data-testid={`voice-deleted-${voice.id}`}>Recording and transcript deleted.</p>;
  const play = () => api(`/voice-notes/${voice.id}/audio`, { blob: true }).then((b) => setUrl(URL.createObjectURL(b))).catch((e) => { toast.error(e.message); setGone(true); });
  const remove = () => window.confirm("Delete this recording and its transcript? The care note stays.") &&
    api(`/voice-notes/${voice.id}`, { method: "DELETE" }).then(() => { toast("Recording deleted"); setDeleted(true); }).catch((e) => toast.error(e.message));
  return (
    <div className="mt-2" data-testid={`voice-${voice.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        {url ? <audio src={url} controls autoPlay className="max-w-full" data-testid={`voice-audio-${voice.id}`} />
          : gone ? <span className="t-meta">Recording no longer kept</span>
            : <button onClick={play} className="btn btn-quiet btn-sm" data-testid={`voice-play-${voice.id}`}><Play size={15} /> Play recording{voice.duration_s ? ` · ${clock(voice.duration_s)}` : ""}</button>}
        {voice.transcript && <button onClick={() => setOpen(!open)} className="btn btn-quiet btn-sm" data-testid={`voice-transcript-btn-${voice.id}`}>{open ? "Hide transcript" : "Transcript"}</button>}
        {membership.role === "owner" && <button onClick={remove} className="btn btn-quiet btn-sm" aria-label="Delete recording" data-testid={`voice-delete-${voice.id}`}><Trash2 size={15} /></button>}
      </div>
      {open && (
        <div className="mt-2 t-meta whitespace-pre-wrap max-w-prose" data-testid={`voice-transcript-${voice.id}`}>
          {voice.transcript}
          {voice.transcript_original && voice.transcript_original !== voice.transcript && <><p className="mt-2 font-medium text-ink">Original transcript</p>{voice.transcript_original}</>}
        </div>
      )}
    </div>
  );
};

export const saveVoice = async (api, v, link) => {
  if (v.transcript !== null && v.transcript !== v.transcript_original) await api(`/voice-notes/${v.id}`, { method: "PATCH", body: { transcript: v.transcript } });
  await api(`/voice-notes/${v.id}/attach`, { method: "POST", body: link });
};

export const discardVoice = (api, v) => api(`/voice-notes/${v.id}`, { method: "DELETE" }).catch(() => {});

export const useVoiceProgress = (voice, onReady) => {
  const { api } = useOrg();
  const id = voice?.ai_status === "processing" ? voice.id : null;
  const ready = useRef(onReady);
  ready.current = onReady;
  useEffect(() => {
    if (!id) return undefined;
    const t = setInterval(() => api(`/voice-notes/${id}`).then((v) => {
      if (v.ai_status === "processing") return;
      clearInterval(t);
      if (v.ai_status === "ok") toast.success("Transcript and suggestions are ready");
      ready.current(v);
    }).catch(() => {}), 3000);
    return () => clearInterval(t);
  }, [api, id]);
};

export const VoiceStatus = ({ v, onRetry }) => {
  const { api } = useOrg();
  const retry = () => api(`/voice-notes/${v.id}/retry`, { method: "POST" }).then(onRetry).catch((e) => toast.error(e.message));
  if (v.ai_status === "processing") return <p className="t-meta mt-1 flex items-center gap-2" data-testid="voice-processing"><Loader2 size={15} className="animate-spin" /> Recording saved. Transcribing… You can leave this screen; it'll be here when you come back.</p>;
  if (v.ai_status === "ok") return <p className="t-meta mt-1" data-testid="voice-ready">The note below is an AI summary. Check it against the transcript before saving.</p>;
  if (v.ai_status === "failed") return (
    <div className="mt-1" data-testid="voice-ai-failed"><p className="t-meta">{v.ai_error} The recording is still attached.</p>
      <button onClick={retry} className="btn btn-quiet btn-sm mt-1" data-testid="voice-retry-btn">Try transcription again</button></div>
  );
  if (v.ai_status === "skipped") return <p className="t-meta mt-1" data-testid="voice-skipped">Saved without transcription.</p>;
  return null;
};
